<template>
  <section id="waitlist" class="waitlist-form">
    <div class="form-container">
      <h2>Help Us Build Pabu for You</h2>
      <p class="subtitle">Share your challenges and we'll tailor Pabu to solve them</p>

      <form 
        v-if="!showSuccess" 
        @submit.prevent="handleSubmit"
        id="waitlistForm"
      >
        <div class="form-group">
          <label for="business">What business do you run? *</label>
          <input 
            type="text" 
            id="business" 
            v-model="formData.business"
            required 
            placeholder="e.g., Home bakery, Online boutique, Mobile esthetician"
          >
        </div>

        <div class="form-group">
          <label for="topQuestion">What's the #1 question you'd want Pabu to answer? *</label>
          <select 
            id="topQuestion" 
            v-model="formData.topQuestion"
            required
          >
            <option value="">Select the question that matters most</option>
            <option value="pricing">Should I raise my prices?</option>
            <option value="profitable">Which products/services are most profitable?</option>
            <option value="hiring">When should I hire help?</option>
            <option value="scale">Am I ready to scale?</option>
            <option value="demand">What are customers asking for that I don't offer?</option>
            <option value="marketing">Where should I focus my marketing?</option>
            <option value="other">Other (please specify)</option>
          </select>
        </div>

        <div 
          class="form-group" 
          v-show="formData.topQuestion === 'other'"
        >
          <label for="otherQuestion">Please specify your question</label>
          <textarea 
            id="otherQuestion" 
            v-model="formData.otherQuestion"
            :required="formData.topQuestion === 'other'"
            placeholder="What would you most want to know about your business?"
          ></textarea>
        </div>

        <div class="form-group">
          <label for="email">Email *</label>
          <input 
            type="email" 
            id="email" 
            v-model="formData.email"
            required 
            placeholder="your@email.com"
          >
        </div>

        <div v-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </div>

        <button 
          type="submit" 
          class="submit-button"
          :disabled="isSubmitting"
        >
          {{ isSubmitting ? 'Submitting...' : 'Join the Beta Program' }}
        </button>
      </form>

      <div 
        class="success-message" 
        :class="{ show: showSuccess }"
      >
        <h3>🎉 You're on the list!</h3>
        <p>Thank you for joining our beta program. We'll be in touch soon to discuss how Pabu can help your business grow.</p>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, reactive, nextTick } from 'vue';

// Configuration - update with your actual API endpoint
const API_ENDPOINT = 'https://services.mviaso.com/api/email/waitlist';

const formData = reactive({
  business: '',
  topQuestion: '',
  otherQuestion: '',
  email: ''
});

const isSubmitting = ref(false);
const showSuccess = ref(false);
const errorMessage = ref('');

const handleSubmit = async () => {
  isSubmitting.value = true;
  errorMessage.value = '';
  
  // Prepare submission data
  const submitData = {
    business: formData.business,
    topQuestion: formData.topQuestion,
    otherQuestion: formData.topQuestion === 'other' ? formData.otherQuestion : '',
    email: formData.email,
    timestamp: new Date().toISOString()
  };
  
  try {
    const response = await fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(submitData)
    });

    if (!response.ok) {
      // Handle HTTP errors
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Server error: ${response.status}`);
    }

    const result = await response.json();
    console.log('Form submitted successfully:', result);
    
    // Show success message
    showSuccess.value = true;
    
    // Reset form
    formData.business = '';
    formData.topQuestion = '';
    formData.otherQuestion = '';
    formData.email = '';
    
    // Scroll to success message
    await nextTick();
    document.querySelector('.success-message')?.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'center' 
    });

  } catch (error) {
    console.error('Form submission error:', error);
    errorMessage.value = error.message || 'Something went wrong. Please try again.';
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>

.error-message {
  color: #dc2626;
  background-color: #fee2e2;
  border: 1px solid #fecaca;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 14px;
}

.success-message {
  display: none;
}

.success-message.show {
  display: block;
}

</style>